<script setup>
import AnButton from "@/components/common/AnButton.vue";
import ProgressBar from "@/sections/my-goals/ProgressBar.vue";
import { useUserStore } from "@/store/user-store";
import { computed, ref } from "vue";
import { chartColors } from "@/services/constant-services";
import AnLoader from "@/components/common/AnLoader.vue";
import { Knob, InputNumber, Tag, Badge, Slider } from "primevue";
import Chart from "primevue/chart";

const { setSection } = defineProps({
  setSection: {
    type: Function,
  },
});

const store = useUserStore();

// Accordion state for allocation methodology
const isAllocationMethodologyExpanded = ref(false);

const toggleAllocationMethodology = () => {
  isAllocationMethodologyExpanded.value = !isAllocationMethodologyExpanded.value;
};

// Accordion state for each goal's current assets contribution
const expandedGoalAssets = ref({});

// Format amounts in lakhs and crores for better readability
const formatAmount = (amount) => {
  if (!amount || amount === 0) return '₹0';

  const absAmount = Math.abs(amount);

  if (absAmount >= 10000000) { // 1 crore or more
    return `₹${(amount / 10000000).toFixed(1)}Cr`;
  } else if (absAmount >= 100000) { // 1 lakh or more
    return `₹${(amount / 100000).toFixed(1)}L`;
  } else if (absAmount >= 1000) { // 1 thousand or more
    return `₹${(amount / 1000).toFixed(0)}K`;
  } else {
    return `₹${amount.toLocaleString('en-IN')}`;
  }
};

// New API response structure
const goalSummaryNew = computed(() => store.goalSummary || null);
console.log('Goal Summary:', goalSummaryNew.value);
console.log('SIP Schedule:', goalSummaryNew.value?.modes?.perGoalSIP?.sipScheduleOverTime);
const meta = computed(() => goalSummaryNew.value?.meta || null);
const perGoalMode = computed(() => goalSummaryNew.value?.modes?.perGoalSIP || null);

// Current mode is always per-goal
const currentMode = computed(() => perGoalMode.value);

// Calculate the actual last SIP date from all goals
const actualLastSipDate = computed(() => {
  if (!currentMode.value?.goals || currentMode.value.goals.length === 0) return null;

  // Find the latest SIP end date among all goals
  const endDates = currentMode.value.goals.map(goal => new Date(goal.sipDuration.endDate));
  const maxDate = new Date(Math.max(...endDates));
  return maxDate.toISOString().split('T')[0];
});

// Calculate actual SIP duration in years
const actualSipDurationYears = computed(() => {
  if (!currentMode.value?.sipStartDate || !actualLastSipDate.value) return 0;

  const startDate = new Date(currentMode.value.sipStartDate);
  const endDate = new Date(actualLastSipDate.value);
  const diffInMonths = (endDate.getFullYear() - startDate.getFullYear()) * 12 + (endDate.getMonth() - startDate.getMonth());
  return (diffInMonths / 12).toFixed(1);
});

// Editable expected returns
const customReturns = ref({});
const returnBounds = computed(() => meta.value?.returnBounds || {});

// Horizon-based return settings
const horizonReturns = ref({
  shortTerm: 6,
  mediumTerm: 9,
  longTerm: 11
});

// Asset allocation example data
const allocationExample = computed(() => {
  if (!meta.value) return null;

  const currentAssets = meta.value.currentFinancialAssets;
  const goals = currentMode.value?.goals || [];

  if (goals.length === 0) return null;

  const totalTarget = goals.reduce((sum, goal) => sum + goal.targetFV, 0);

  return {
    currentAssets,
    totalTarget,
    goals: goals.map(goal => ({
      name: goal.goalName,
      target: goal.targetFV,
      weight: ((goal.targetFV / totalTarget) * 100).toFixed(1),
      allocatedAssets: goal.currentAssetsContribution.allocatedAmount,
      futureValue: goal.currentAssetsContribution.futureValue,
      adjustedTarget: goal.currentAssetsContribution.adjustedTarget
    }))
  };
});

// Legacy data fallback
const goals = computed(() => store.dashboardData?.custGoals || []);
const goalDetails = computed(() => store.goalSummary?.customerGoalsDetails || null);

// Define bannerData as computed property for reactivity
const bannerData = computed(() => [
  {
    id: 1,
    label: "Total Goals",
    value: currentMode.value?.goals?.length || 0,
    color: "var(--an-primary)",
    tooltip: "The total number of goals you have set."
  },
  {
    id: 2,
    label: "Total Investment",
    value: formatAmount(perGoalMode.value?.totalInvestmentBreakdown?.totalInvestmentOverEntirePeriod || 0),
    color: "var(--an-success)",
    tooltip: "The total amount you have invested towards your goals."
  },
  {
    id: 3,
    label: "Total SIP Contribution",
    value: formatAmount(perGoalMode.value?.totalInvestmentBreakdown?.totalSipContribution || 0),
    color: "var(--an-warning)",
    tooltip: "The total SIP contribution across all your goals."
  },
  {
    id: 4,
    label: "Current Financial Assets", // Updated label
    value: formatAmount(perGoalMode.value?.totalInvestmentBreakdown?.currentAssetsContribution || 0),
    color: "var(--an-info)",
    tooltip: "This comprises the value of Stocks, Mutual Funds & Fixed Income instruments." // Updated tooltip
  }
]);

// Tab state for chart selection
const selectedChartTab = ref('sip'); // 'sip' or 'portfolio'

// Computed properties
const latestSummary = computed(() => {
  return props.summary?.at(-1) || null;
});

// Add this computed property to check if charts should be rendered
const isChartDataReady = computed(() => {
  const schedule = currentMode.value?.sipScheduleOverTime?.scheduleBreakdown;
  const hasScheduleData = schedule && Array.isArray(schedule) && schedule.length > 0;
  const hasGoals = currentMode.value?.goals && currentMode.value.goals.length > 0;
  const hasSummary = !!currentMode.value;

  return hasScheduleData && hasGoals && hasSummary;
});

// SIP schedule chart data
const sipScheduleChartData = computed(() => {
  // Early return if data is not ready
  if (!isChartDataReady.value) {
    console.log('Chart data not ready yet');
    return null;
  }

  const schedule = currentMode.value?.sipScheduleOverTime?.scheduleBreakdown;
  if (!schedule || !Array.isArray(schedule) || schedule.length === 0) {
    console.log('No schedule data available');
    return null;
  }

  // Ensure we have valid data before proceeding
  try {
    // Filter data to show the first 3 months and then every 3 months
    const filteredSchedule = schedule.filter((item, index) => index < 3 || index % 3 === 0);

    return {
      labels: filteredSchedule.map(item => item.date),
      datasets: [{
        label: 'Total Active SIPs',
        data: filteredSchedule.map(item => item.monthlySipTotal),
        borderColor: chartColors[0],
        backgroundColor: chartColors[0] + '20',
        tension: 0.4,
        fill: true
      }]
    };
  } catch (error) {
    console.error('Error creating chart data:', error);
    return null;
  }
});

// Portfolio Value Over Time Chart Data
const portfolioValueChartData = computed(() => {
  // Early return if data is not ready
  if (!currentMode.value?.goals || !meta.value) {
    console.log('Portfolio chart data not ready');
    return null;
  }

  const startDate = new Date(currentMode.value.sipStartDate);
  const schedule = currentMode.value.sipScheduleOverTime?.scheduleBreakdown || [];
  const milestones = currentMode.value.cashFlows?.keyMilestones || [];

  // Get historical data from portfolioDailySummary
  const historicalData = store.dashboardData?.portfolioDailySummary || [];
  const currentPortfolioValue = meta.value.currentFinancialAssets;

  // Calculate weighted average return across all goals
  const totalTarget = currentMode.value.goals.reduce((sum, g) => sum + g.targetFV, 0);
  const weightedReturn = currentMode.value.goals.reduce((sum, g) => {
    const weight = g.targetFV / totalTarget;
    return sum + (g.expectedReturn * weight);
  }, 0);

  const monthlyReturn = Math.pow(1 + weightedReturn, 1/12) - 1;

  // Build timeline data
  const timelineData = [];
  let portfolioValue = currentPortfolioValue;
  let cumulativeSipInvested = 0;

  // Add current point
  timelineData.push({
    date: startDate.toISOString().substring(0, 7),
    portfolioValue: portfolioValue,
    cumulativeSipInvested: 0,
    totalInvested: currentPortfolioValue,
    label: 'Today'
  });

  // Process each month from schedule
  schedule.forEach((item, index) => {
    // Apply monthly return to existing portfolio
    portfolioValue = portfolioValue * (1 + monthlyReturn);

    // Add SIP contribution
    const sipAmount = item.monthlySipTotal;
    portfolioValue += sipAmount;
    cumulativeSipInvested += sipAmount;

    // Store portfolio value before withdrawal for display
    const portfolioValueBeforeWithdrawal = portfolioValue;

    // Check for goal completions (withdrawals)
    const itemDate = new Date(item.date + '-01');
    const completingMilestone = milestones.find(m => {
      const mDate = new Date(m.date);
      return mDate.getFullYear() === itemDate.getFullYear() &&
             mDate.getMonth() === itemDate.getMonth() &&
             m.event.includes('SIP Stops');
    });

    let hasCompletingGoals = false;
    let completingGoalTargets = [];
    if (completingMilestone && item.completingGoals.length > 0) {
      hasCompletingGoals = true;
      // Find the goal that's completing and store its target
      item.completingGoals.forEach(goalName => {
        const completingGoal = currentMode.value.goals.find(g => g.goalName === goalName);
        if (completingGoal) {
          completingGoalTargets.push({
            name: goalName,
            target: completingGoal.targetFV
          });
          // Subtract goal target from portfolio (withdrawal for goal achievement)
          portfolioValue -= completingGoal.targetFV;
        }
      });
    }

    // Add data point (show every 3 months after first 3, OR if it's a milestone)
    if (index < 3 || index % 3 === 0 || hasCompletingGoals) {
      timelineData.push({
        date: item.date,
        portfolioValue: Math.max(0, portfolioValueBeforeWithdrawal), // Show value BEFORE withdrawal
        portfolioValueAfterWithdrawal: Math.max(0, portfolioValue), // Actual value after withdrawal (for internal tracking)
        cumulativeSipInvested: cumulativeSipInvested,
        totalInvested: currentPortfolioValue + cumulativeSipInvested,
        activeGoals: item.activeGoalsCount,
        completingGoals: hasCompletingGoals ? item.completingGoals : [],
        completingGoalTargets: hasCompletingGoals ? completingGoalTargets : []
      });
    }
  });

  // Create milestone markers dataset - position at the same point as portfolio value
  const milestoneData = timelineData.map(d => {
    if (d.completingGoals && d.completingGoals.length > 0) {
      // Use the portfolio value (which is already before withdrawal)
      return d.portfolioValue;
    }
    return null;
  });

  return {
    labels: timelineData.map(d => d.date),
    datasets: [
      {
        label: 'Portfolio Value',
        data: timelineData.map(d => d.portfolioValue),
        borderColor: chartColors[2],
        backgroundColor: chartColors[2] + '20',
        tension: 0.4,
        fill: true,
        borderWidth: 3,
        pointRadius: 0,
        pointHoverRadius: 6
      },
      {
        label: 'Total Invested',
        data: timelineData.map(d => d.totalInvested),
        borderColor: chartColors[1],
        backgroundColor: chartColors[1] + '10',
        tension: 0.4,
        fill: false,
        borderWidth: 2,
        borderDash: [5, 5],
        pointRadius: 0,
        pointHoverRadius: 6
      },
      {
        label: 'Goal Achieved',
        data: milestoneData,
        borderColor: 'transparent',
        backgroundColor: 'rgb(34, 197, 94)',
        pointRadius: 8,
        pointHoverRadius: 10,
        pointStyle: 'star',
        pointBorderWidth: 2,
        pointBorderColor: 'rgb(34, 197, 94)',
        showLine: false
      }
    ],
    timelineData
  };
});

// Define chart options used by the SIP line chart
const enhancedLineChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: 'rgba(0,0,0,0.85)',
      titleColor: '#fff',
      bodyColor: '#fff',
      padding: 10,
      cornerRadius: 6,
      callbacks: {
        label: function(context) {
          const value = context.parsed.y || 0;
          return `Monthly SIP: ${formatAmount(value)}`;
        }
      }
    }
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { maxRotation: 0, autoSkip: true }
    },
    y: {
      grid: { color: '#f0f0f0' },
      ticks: {
        callback: function(value) {
          return formatAmount(Number(value));
        }
      }
    }
  }
};

// Chart options for portfolio value chart
const portfolioChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: {
      display: false
    },
    tooltip: {
      backgroundColor: 'rgba(0,0,0,0.85)',
      titleColor: '#fff',
      bodyColor: '#fff',
      padding: 12,
      cornerRadius: 6,
      filter: function(tooltipItem) {
        // Don't show tooltip for milestone dataset unless it has a value
        return tooltipItem.datasetIndex !== 2 || tooltipItem.parsed.y !== null;
      },
      callbacks: {
        label: function(context) {
          const value = context.parsed.y || 0;
          const datasetLabel = context.dataset.label;

          if (context.datasetIndex === 2 && value) {
            // Milestone marker
            if (portfolioValueChartData.value?.timelineData) {
              const dataPoint = portfolioValueChartData.value.timelineData[context.dataIndex];
              if (dataPoint.completingGoalTargets && dataPoint.completingGoalTargets.length > 0) {
                // Show each goal with its target amount
                const goalDetails = dataPoint.completingGoalTargets
                  .map(gt => `${gt.name} - ${formatAmount(gt.target)}`)
                  .join(', ');
                return `🎯 ${goalDetails}`;
              }
            }
          }

          return `${datasetLabel}: ${formatAmount(value)}`;
        }
      }
    }
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { maxRotation: 0, autoSkip: true }
    },
    y: {
      grid: { color: '#f0f0f0' },
      ticks: {
        callback: function(value) {
          return formatAmount(Number(value));
        }
      }
    }
  }
};

const getHorizonCategory = (years) => {
  if (years <= 3) return 'shortTerm';
  if (years <= 7) return 'mediumTerm';
  return 'longTerm';
};

const updateHorizonReturn = (horizon, value) => {
  horizonReturns.value[horizon] = value;
  // Update all goals in this horizon
  if (currentMode.value?.goals) {
    currentMode.value.goals.forEach(goal => {
      const category = getHorizonCategory(goal.horizonYears);
      if (category === horizon) {
        updateCustomReturn(goal.goalName, value);
      }
    });
  }
};

const updateCustomReturn = (goalName, value) => {
  customReturns.value[goalName] = value / 100; // Convert percentage to decimal
};

const recalculateWithCustomReturns = async () => {
  try {
    // Prepare custom returns payload with ALL goal names and their current expected returns
    const customReturnsPayload = {};

    // Include all goals with their current expected returns (either modified or original)
    if (currentMode.value?.goals) {
      currentMode.value.goals.forEach(goal => {
        const goalName = goal.goalName;
        // Use custom return if modified, otherwise use the current expectedReturnPercent
        if (customReturns.value[goalName] !== undefined) {
          customReturnsPayload[goalName] = customReturns.value[goalName]; // Already in decimal format
        } else {
          customReturnsPayload[goalName] = goal.expectedReturnPercent / 100; // Convert percentage to decimal
        }
      });
    }

    // Call loadGoalSummary with customReturns payload
    const payload = {
      customReturns: customReturnsPayload
    };

    console.log('Recalculating with custom returns for all goals:', payload);
    await store.loadGoalSummary(payload);

    // Clear custom returns after successful API call
    customReturns.value = {};

  } catch (error) {
    console.error('Error recalculating with custom returns:', error);
  }
};

const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};
</script>

<template>
  <AnLoader v-if="store.isDashboardLoading" />
  <div v-else class="goal-dashboard">
    <!-- Header Section with toggle
    <div class="header-wrapper">
      <div class="header-content">
        <div class="title-section">
          <i class="pi pi-flag text-primary font-24"></i>
          <h1 class="font-24 font-weight-700 m-0">Goal Planning Dashboard</h1>
        </div>
      </div>
    </div>-->

    <!-- Progress Bar -->
    <div v-if="goals.length > 0" class="progress-bar-section">
      <ProgressBar :goals="goals" />
    </div>

    <!-- No Goals State -->
    <div
      v-if="!goalSummaryNew && (!goalDetails || goals.length === 0)"
      class="d-flex flex-direction-column align-items-center mt-40"
    >
      <img
        src="@/assets/images/no-goal.png"
        width="177px"
        height="176px"
        alt="no-goal"
      />
      <span class="mt-20 text-black font-20 font-weight-500">
        You haven't set any goal! Please set new goal
      </span>
      <AnButton
        label="Set New Goal"
        variant="primary"
        class="mt-20"
        @click="() => setSection(2)"
      />
    </div>

    <!-- Main Dashboard Content -->
    <div v-if="goalSummaryNew">
      <!-- Financial Assets Overview Banner -->
      <section class="w-100 mt-30 d-flex flex-direction-column input-shadow br-10 pv-20">
        <div class="flex-1 br-10 d-flex">
          <template v-for="(item, index) in bannerData" :key="item.id">
            <div class="d-flex flex-direction-column justify-center p-5 gap-5 flex-1">
              <span class="text-center font-weight-600 d-flex justify-center align-items-center gap-5">
                {{ item.label }}
                <i
                  class="pi pi-info-circle font-15"
                  v-if="item.tooltip"
                  v-tooltip.top="{ value: item.tooltip, class: 'bottom-arrow' }"
                />
              </span>
              <span
                class="font-25 font-weight-500 text-center"
                :style="{ color: item.color || 'var(--an-primary)' }"
              >
                {{ item.value }}
              </span>
            </div>
            <span class="v-divider" v-if="index < bannerData.length - 1" :key="`divider-${index}`" />
          </template>
        </div>
      </section>

      <!-- No Goals Motivational State -->
      <div v-if="!currentMode?.goals || currentMode.goals.length === 0" class="no-goals-motivational mt-40">
        <div class="motivational-card input-shadow br-12 p-40">
          <div class="motivational-icon mb-20">
            <i class="pi pi-flag text-primary" style="font-size: 4rem;"></i>
          </div>
          <h2 class="font-28 font-weight-700 text-primary mb-15 text-center">Start Your Financial Journey</h2>
          <p class="font-16 text-gray text-center mb-30 max-width-600">
            Transform your dreams into achievable goals! Whether it's buying a home, funding education,
            planning retirement, or taking that dream vacation - set your goals and let us help you get there.
          </p>

          <div class="goal-benefits d-grid gap-20 mb-40">
            <div class="benefit-item d-flex align-items-start gap-15">
              <div class="benefit-icon bg-success br-circle p-12">
                <i class="pi pi-chart-line text-white font-18"></i>
              </div>
              <div>
                <h4 class="font-16 font-weight-600 mb-5">Smart SIP Planning</h4>
                <p class="font-14 text-gray mb-0">Get personalized monthly SIP recommendations based on your goals and timeline</p>
              </div>
            </div>

            <div class="benefit-item d-flex align-items-start gap-15">
              <div class="benefit-icon bg-primary br-circle p-12">
                <i class="pi pi-wallet text-white font-18"></i>
              </div>
              <div>
                <h4 class="font-16 font-weight-600 mb-5">Optimize Current Assets</h4>
                <p class="font-14 text-gray mb-0">Your existing investments work towards your goals with intelligent allocation</p>
              </div>
            </div>

            <div class="benefit-item d-flex align-items-start gap-15">
              <div class="benefit-icon bg-warning br-circle p-12">
                <i class="pi pi-calendar text-white font-18"></i>
              </div>
              <div>
                <h4 class="font-16 font-weight-600 mb-5">Track Progress</h4>
                <p class="font-14 text-gray mb-0">Monitor your journey with detailed projections and milestone tracking</p>
              </div>
            </div>
          </div>

          <div class="d-flex justify-center">
            <AnButton
              label="Set Your First Goal"
              variant="primary"
              icon="pi pi-plus"
              size="large"
              @click="() => setSection(2)"
            />
          </div>
        </div>
      </div>

      <!-- Existing Goal Content - Only show when goals exist -->
      <template v-if="currentMode?.goals && currentMode.goals.length > 0">
        <!-- Charts Section with Tabs -->
        <div class="chart-wrapper mt-30 d-flex gap-20">
          <div class="input-shadow line p-20 br-10 d-flex flex-direction-column w-100 chart-container-dynamic">
            <!-- Tab Headers -->
            <div class="chart-tabs d-flex gap-10 mb-10">
              <button
                :class="['chart-tab', { active: selectedChartTab === 'sip' }]"
                @click="selectedChartTab = 'sip'"
              >
                <i class="pi pi-chart-bar mr-5"></i>
                SIP Growth Over Time
              </button>
              <button
                :class="['chart-tab', { active: selectedChartTab === 'portfolio' }]"
                @click="selectedChartTab = 'portfolio'"
                v-tooltip.top="{ value: 'Shows projected portfolio value with SIP contributions and goal withdrawals', class: 'chart-tab-tooltip' }"
              >
                <i class="pi pi-chart-line mr-5"></i>
                Your Investment Value Over Time
                <i class="pi pi-info-circle font-11 ml-5" style="opacity: 0.7;"></i>
              </button>
            </div>

            <!-- SIP Chart Tab -->
            <div v-if="selectedChartTab === 'sip'" class="d-flex flex-direction-column flex-1 chart-tab-content">
              <div v-if="sipScheduleChartData" class="chart-content" style="overflow-x: auto;">
                <Chart
                  v-if="sipScheduleChartData && sipScheduleChartData.labels && sipScheduleChartData.labels.length > 0"
                  type="line"
                  :data="sipScheduleChartData"
                  :options="enhancedLineChartOptions"
                  class="line-chart w-100"
                  style="min-width: 600px; height: 250px;"
                />
                <div v-else class="chart-placeholder">
                  <i class="pi pi-chart-line"></i>
                  <p>Loading SIP schedule chart...</p>
                </div>
              </div>
              <div v-else class="chart-placeholder">
                <i class="pi pi-chart-line font-40 text-gray"></i>
                <p class="font-14 text-gray mt-10">No SIP schedule data available</p>
              </div>
            </div>

            <!-- Portfolio Value Chart Tab -->
            <div v-if="selectedChartTab === 'portfolio'" class="d-flex flex-direction-column flex-1 chart-tab-content">
              <div v-if="portfolioValueChartData" class="chart-content" style="overflow-x: auto;">
                <!-- Custom Legend -->
                <div class="custom-chart-legend d-flex justify-center gap-15 mb-3">
                  <div class="legend-item d-flex align-items-center gap-3">
                    <span class="legend-dot" style="background-color: #f97316;"></span>
                    <span class="font-11">Portfolio Value</span>
                  </div>
                  <div class="legend-item d-flex align-items-center gap-3">
                    <span class="legend-dot legend-dot-dashed" style="border-color: #16a34a;"></span>
                    <span class="font-11">Total Invested</span>
                  </div>
                  <div class="legend-item d-flex align-items-center gap-3">
                    <span class="legend-star">⭐</span>
                    <span class="font-11">Goal Achieved</span>
                  </div>
                </div>

                <Chart
                  v-if="portfolioValueChartData && portfolioValueChartData.labels && portfolioValueChartData.labels.length > 0"
                  type="line"
                  :data="portfolioValueChartData"
                  :options="portfolioChartOptions"
                  class="line-chart w-100"
                  style="min-width: 600px; height: 260px;"
                />
                <div v-else class="chart-placeholder">
                  <i class="pi pi-chart-line"></i>
                  <p>Loading portfolio chart...</p>
                </div>
              </div>
              <div v-else class="chart-placeholder">
                <i class="pi pi-chart-line font-40 text-gray"></i>
                <p class="font-14 text-gray mt-10">No portfolio data available</p>
              </div>
            </div>
          </div>
        </div>

      <!-- SIP Information Cards -->
      <div class="sip-cards-wrapper mt-30">
        <span class="font-20 font-weight-700 mb-20">Overall SIP Details across Goals</span>
        <div class="sip-metrics d-grid gap-20">
          <div class="metric-card input-shadow br-10 p-20 d-flex flex-direction-column align-items-center">
            <span class="metric-label font-14 text-center mb-10">Starting SIP</span>
            <span class="metric-value font-20 font-weight-700 text-primary text-center">
              {{ formatAmount(perGoalMode?.totalPerGoalSip) }}
            </span>
          </div>

          <div class="metric-card input-shadow br-10 p-20 d-flex flex-direction-column align-items-center">
            <span class="metric-label font-14 text-center mb-10">SIP Duration</span>
            <span class="metric-value font-16 font-weight-700 text-primary text-center">
              {{ actualSipDurationYears }} years
            </span>
            <span class="font-12 text-center text-gray mt-5">
              {{ formatDate(currentMode?.sipStartDate) }} to {{ formatDate(actualLastSipDate) }}
            </span>
          </div>

          <div class="metric-card input-shadow br-10 p-20 d-flex flex-direction-column align-items-center">
            <span class="metric-label font-14 text-center mb-10">Final Monthly SIP</span>
            <span class="metric-value font-20 font-weight-700 text-success text-center">
              {{ formatAmount(perGoalMode?.sipScheduleOverTime?.finalMonthlySip) }}
            </span>
          </div>
        </div>
      </div>

      <!-- Expected Returns Configuration with Dial Gauges -->
      <div class="mt-30">
        <div class="d-flex justify-between align-items-center mb-20">
          <span class="font-20 font-weight-700">Fine-tune Returns By Goals</span>
        </div>
        <div class="input-shadow br-10 p-20">
          <!-- Horizon-based Return Dials -->

          <!-- Individual Goal Returns -->
          <div class="goal-returns">
            <div class="d-flex justify-between align-items-center mb-15">
              <p class="font-14 text-gray">
                Adjust returns based on your risk appetite and market outlook. Use conservative returns (4-10%) for near-term goals and higher returns (8-18%) for long-term goals.
              </p>
            </div>
            <div class="goal-returns-grid d-grid gap-15 mb-15">
              <div
                v-for="goal in currentMode?.goals"
                :key="goal.goalName"
                class="goal-return-item bg-light br-6 p-12 d-flex justify-between align-items-center"
              >
                <div class="goal-info">
                  <span class="font-14 font-weight-500">{{ goal.goalName }}</span>
                  <span class="font-12 text-gray d-block">({{ goal.horizonYears }} years)</span>
                </div>
                <div class="return-input d-flex align-items-center gap-10">
                  <Slider
                    :model-value="goal.expectedReturnPercent"
                    @update:model-value="(value) => updateCustomReturn(goal.goalName, value)"
                    :min="returnBounds[getHorizonCategory(goal.horizonYears)]?.minReturn"
                    :max="returnBounds[getHorizonCategory(goal.horizonYears)]?.maxReturn"
                    :step="0.1"
                    class="slider"
                    style="width: 60px"
                  />
                  <div class="input-number-wrapper">
                    <InputNumber
                      :model-value="goal.expectedReturnPercent"
                      @update:model-value="(value) => updateCustomReturn(goal.goalName, value)"
                      suffix="%"
                      :min="returnBounds[getHorizonCategory(goal.horizonYears)]?.minReturn"
                      :max="returnBounds[getHorizonCategory(goal.horizonYears)]?.maxReturn"
                      :step="0.1"
                      show-buttons
                      button-layout="vertical"
                      size="small"
                      style="width: 100px"
                    />
                  </div>
                </div>
              </div>
            </div>
            <AnButton
              label="Recalculate with Custom Returns"
              icon="pi pi-refresh"
              variant="primary"
              size="small"
              @click="recalculateWithCustomReturns"
            />
          </div>
        </div>
      </div>

      <!-- Goals Details -->
      <div class="mt-30">
        <div class="d-flex justify-between align-items-center mb-20">
          <span class="font-20 font-weight-700">Goal - SIP & Current Financial Assets Allocation Details</span>
        </div>

        <div class="goals-grid d-grid gap-20">
          <div
            v-for="(goal, index) in currentMode?.goals"
            :key="goal.goalName"
            class="goal-card input-shadow br-10 p-20"
          >
            <div class="goal-header d-flex align-items-center gap-20 mb-15">
              <div
                class="goal-indicator"
                :style="{ backgroundColor: chartColors[index % chartColors.length] }"
              ></div>
              <div class="flex-1">
                <h4 class="font-16 font-weight-600">{{ goal.goalName }}</h4>
                <div class="goal-meta d-flex gap-15 mt-5">
                  <span class="font-12 text-gray">{{ goal.horizonYears }} years</span>
                  <span class="font-12 text-gray">{{ goal.expectedReturnPercent }}% expected return</span>
                </div>
              </div>
            </div>

            <div class="goal-details mb-15">
              <div class="detail-row d-flex justify-between align-items-center pv-8">
                <span class="detail-label font-13 text-gray">Target Amount:</span>
                <span class="detail-value font-13 font-weight-600">{{ formatAmount(goal.targetFV) }}</span>
              </div>
              <div class="detail-row d-flex justify-between align-items-center pv-8">
                <span class="detail-label font-13 text-gray">Monthly SIP:</span>
                <span class="detail-value font-13 font-weight-600">{{ formatAmount(goal.monthlySip) }}</span>
              </div>
              <div class="detail-row d-flex justify-between align-items-center pv-8">
                <span class="detail-label font-13 text-gray">Due Date:</span>
                <span class="detail-value font-13">{{ formatDate(goal.dueDate) }}</span>
              </div>
              <div class="detail-row d-flex justify-between align-items-center pv-8">
                <span class="detail-label font-13 text-gray">SIP End Date:</span>
                <span class="detail-value font-13">{{ formatDate(goal.sipDuration.endDate) }}</span>
              </div>
              <div class="detail-row d-flex justify-between align-items-center pv-8">
                <span class="detail-label font-13 text-gray">SIP Duration:</span>
                <span class="detail-value font-13">{{ goal.sipDuration.durationYears }} years ({{ goal.sipDuration.durationMonths }} months)</span>
              </div>
            </div>

            <!-- Current Assets Contribution -->
            <div class="h-divider mb-15"></div>
            <div class="assets-contribution-accordion">
              <div
                class="assets-accordion-header cursor-pointer d-flex justify-between align-items-center p-12 bg-light br-6"
                @click="expandedGoalAssets[goal.goalName] = !expandedGoalAssets[goal.goalName]"
              >
                <h5 class="font-14 font-weight-600 mb-0">Current Assets Contribution</h5>
                <i :class="`pi ${expandedGoalAssets[goal.goalName] ? 'pi-chevron-up' : 'pi-chevron-down'} font-12`"></i>
              </div>

              <div v-if="expandedGoalAssets[goal.goalName]" class="assets-accordion-content bg-light br-6 p-12 mt-2">
                <div class="contribution-details d-flex flex-direction-column gap-6">
                  <!-- Fixed weight calculation to display percentage -->
                  <div class="contribution-row d-flex justify-between align-items-center">
                    <span class="font-12">Weight:</span>
                    <span class="font-12 font-weight-500">
                      {{ formatAmount(goal.targetFV) }} ÷ {{ formatAmount(allocationExample.totalTarget) }} = {{ ((goal.targetFV / allocationExample.totalTarget) * 100).toFixed(1) }}%
                    </span>
                  </div>
                  <div class="contribution-row d-flex justify-between align-items-center">
                    <span class="font-12">Allocated Amount:</span>
                    <span class="font-12 font-weight-500">
                      {{ formatAmount(goal.currentAssetsContribution.allocatedAmount) }}
                    </span>
                  </div>
                  <div class="contribution-row d-flex justify-between align-items-center">
                    <span class="font-12">Future Value:</span>
                    <span class="font-12 font-weight-500 text-success">
                      {{ formatAmount(goal.currentAssetsContribution.futureValue) }}
                    </span>
                  </div>
                  <div class="contribution-row d-flex justify-between align-items-center">
                    <span class="font-12">SIP Required For:</span>
                    <span class="font-12 font-weight-500 text-primary">
                      {{ formatAmount(goal.currentAssetsContribution.adjustedTarget) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Key Milestones -->
      <div class="mt-30">
        <div class="d-flex justify-between align-items-center mb-20">
          <span class="font-20 font-weight-700">Key Milestones</span>
        </div>

        <div class="input-shadow br-10 p-20">
          <div class="cashflow-timeline d-flex flex-direction-column gap-15">
            <div
              v-for="(milestone, index) in currentMode?.cashFlows?.keyMilestones"
              :key="index"
              class="milestone-item d-flex gap-15 align-items-center p-15 bg-light br-8"
            >
              <div class="milestone-date bg-primary text-white p-10 br-6 font-12 font-weight-600 text-center" style="min-width: 80px;">
                {{ formatDate(milestone.date) }}
              </div>
              <div class="milestone-content flex-1">
                <div class="milestone-header d-flex justify-between align-items-center">
                  <span class="font-14 font-weight-600">{{ milestone.event }}</span>
                  <Tag
                    v-if="milestone.event.includes('Achievement')"
                    value="Goal Complete"
                    severity="success"
                    size="small"
                  />
                </div>
                <!-- Combine all texts into a single line with light borders -->
                <div class="milestone-details d-flex flex-wrap gap-2">
                  <span class="milestone-text">{{ milestone.description }}</span>
                  <span class="milestone-text">SIP Amount: {{ milestone.sipAmount && milestone.sipAmount !== 'Variable (Step-up)' ? formatAmount(milestone.sipAmount) : 'N/A' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Button -->
      <div class="d-flex justify-center mt-30">
        <AnButton
          label="Manage Goals"
          variant="primary"
          icon="pi pi-cog"
          @click="() => setSection(2)"
        />
      </div>

      <!-- Allocation Methodology Note -->
      <div class="mt-40">
        <div class="allocation-methodology-card input-shadow br-12">
          <div
            class="methodology-header cursor-pointer"
            @click="toggleAllocationMethodology"
          >
            <div class="header-content-wrapper">
              <div class="icon-wrapper">
                <div class="icon-circle">
                  <i class="pi pi-chart-pie"></i>
                </div>
              </div>
              <div class="header-text">
                <h3 class="methodology-title">
                  Current Asset Allocation Methodology
                </h3>
                <p class="methodology-subtitle">
                  {{ isAllocationMethodologyExpanded ? 'Click to collapse' : 'Click to learn how your assets are allocated' }}
                </p>
              </div>
            </div>
            <div class="chevron-icon">
              <i :class="`pi ${isAllocationMethodologyExpanded ? 'pi-chevron-up' : 'pi-chevron-down'}`"></i>
            </div>
          </div>

          <transition name="methodology-expand">
            <div v-if="isAllocationMethodologyExpanded" class="methodology-content">
              <div class="methodology-grid">
                <!-- Proportional Allocation -->
                <div class="methodology-card proportional-card">
                  <div class="card-icon-wrapper">
                    <div class="card-icon bg-gradient-success">
                      <i class="pi pi-percentage"></i>
                    </div>
                  </div>
                  <div class="card-content">
                    <h4 class="card-title">
                      <span class="title-icon">📊</span>
                      Proportional Allocation Strategy
                    </h4>
                    <p class="card-description">
                      Your current financial assets (stocks, mutual funds & fixed income instruments)
                      <span class="highlight-amount">{{ formatAmount(meta?.currentFinancialAssets || 0) }}</span>
                      are intelligently allocated to each goal proportionally based on the goal's target amount relative to the total of all goal targets.
                    </p>
                  </div>
                </div>

                <!-- Unified Return Rate -->
                <div class="methodology-card unified-card">
                  <div class="card-icon-wrapper">
                    <div class="card-icon bg-gradient-info">
                      <i class="pi pi-sync"></i>
                    </div>
                  </div>
                  <div class="card-content">
                    <h4 class="card-title">
                      <span class="title-icon">⚖️</span>
                      Unified Return Rate Assumption
                    </h4>
                    <p class="card-description">
                      Both existing investments and new SIP contributions are assumed to earn the
                      <span class="highlight-text">same return rate per goal</span>,
                      which is reasonable when invested in similar asset classes aligned with each goal's risk profile and time horizon.
                    </p>
                  </div>
                </div>

                <!-- Key Insight -->
                <div class="methodology-card insight-card">
                  <div class="card-icon-wrapper">
                    <div class="card-icon bg-gradient-warning">
                      <i class="pi pi-lightbulb"></i>
                    </div>
                  </div>
                  <div class="card-content">
                    <h4 class="card-title">
                      <span class="title-icon">💡</span>
                      Why This Approach Works
                    </h4>
                    <p class="card-description">
                      This methodology ensures
                      <span class="highlight-text">optimal utilization</span>
                      of your current assets while maintaining
                      <span class="highlight-text">goal-specific risk alignment</span>
                      through targeted expected returns. It's a sophisticated balance between efficiency and personalization.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>

      <!-- Disclaimer -->
      <div class="note p-15 br-8 mt-20 bg-light">
        <span class="font-weight-600">Important Disclaimer</span>
        <ol class="font-weight-500 mt-10">
          <li class="mb-8">
            Projections are based on assumed returns and current market conditions.
            Past performance does not guarantee future results.
          </li>
          <li class="mb-8">
            Investment in securities is subject to market risks. Values may fluctuate
            due to economic, political, and global events.
          </li>
          <li class="mb-8">
            Consider staying invested through market cycles for optimal returns.
            Consult a financial advisor for personalized advice.
          </li>
        </ol>
      </div>
      </template>
    </div>


  </div>
</template><style scoped>
/* No Goals Motivational State */
.no-goals-motivational {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 500px;
}

.motivational-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  max-width: 800px;
  width: 100%;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.motivational-card::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(var(--primary-rgb), 0.05) 0%, transparent 70%);
  pointer-events: none;
}

.motivational-icon {
  position: relative;
  z-index: 1;
}

.max-width-600 {
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.goal-benefits {
  grid-template-columns: 1fr;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  text-align: left;
}

.benefit-item {
  position: relative;
  z-index: 1;
}

.benefit-icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.bg-success {
  background: var(--an-success);
}

.bg-warning {
  background: var(--an-warning);
}

/* Main Container */
.goal-dashboard {
  padding: 20px 24px;
  background: var(--surface-ground);
  min-height: 100vh;
}

/* Header Wrapper */
.header-wrapper {
  background: var(--surface-card);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.title-section {
  display: flex;
  align-items: center;
  gap: 12px;
}

.title-section h1 {
  color: var(--text-color);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
}

/* Progress Bar Section */
.progress-bar-section {
  margin: 30px 0;
  padding: 20px 0;
}

/* No Goals State */
.no-goals-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  margin-top: 40px;
}

.empty-state-card {
  background: var(--surface-card);
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  max-width: 400px;
}

.empty-state-card h3 {
  color: var(--text-color);
  margin: 20px 0 10px 0;
}

.empty-state-card p {
  color: var(--text-color-secondary);
  margin: 10px 0 20px 0;
}

/* Dashboard Content */
.dashboard-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.dashboard-grid {
  display: grid;
  gap: 20px;
}

/* Overview Section */
.overview-section {
  background: var(--surface-card);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.overview-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.overview-card {
  background: var(--surface-ground);
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  transition: transform 0.2s ease;
}

.overview-card:hover {
  transform: translateY(-2px);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 12px;
}

.card-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-color-secondary);
}

.card-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
}

/* Chart Container */
.chart-container {
  background: var(--surface-card);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-top: 20px;
}

.chart-wrapper {
  position: relative;
}

/* Dynamic chart container that adapts to content */
.chart-container-dynamic {
  min-height: 300px;
  height: auto;
}

.chart-tab-content {
  min-height: 250px;
  height: auto;
}

/* Cards */
.dashboard-card {
  background: var(--surface-card);
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.card-title h3 {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-color);
  margin: 0;
}

.card-title i {
  color: var(--primary-color);
}

/* Goals Grid */
.goals-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

.goal-card {
  background: var(--surface-ground);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  padding: 20px;
  transition: all 0.2s ease;
}

.goal-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: var(--primary-color);
}

.goal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.goal-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.goal-meta {
  display: flex;
  gap: 16px;
  margin-top: 8px;
  font-size: 0.75rem;
  color: var(--text-color-secondary);
}

/* Detail Rows */
.goal-details {
  margin: 16px 0;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid var(--surface-border);
  font-size: 0.875rem;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  color: var(--text-color-secondary);
}

.detail-value {
  font-weight: 600;
  color: var(--text-color);
}

/* Assets Contribution */
.assets-contribution-accordion {
  margin-top: 0;
}

.assets-accordion-header {
  transition: background-color 0.2s ease;
}

.assets-accordion-header:hover {
  background-color: var(--surface-hover) !important;
}

.assets-accordion-content {
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
    overflow: hidden;
  }
  to {
    opacity: 1;
    max-height: 500px;
  }
}

.assets-contribution {
  background: var(--surface-card);
  border-radius: 6px;
  padding: 16px;
  margin-top: 16px;
}

.contribution-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.contribution-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.75rem;
}

/* SIP Information */
.sip-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.metric-item {
  background: var(--surface-ground);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  padding: 16px;
  text-align: center;
}

.metric-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--text-color-secondary);
  margin-bottom: 8px;
}

.metric-value {
  display: block;
  font-size: 1rem;
  font-weight: 700;
  color: var(--primary-color);
}

/* Timeline */
.cashflow-timeline {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.milestone-item {
  display: flex;
  gap: 15px;
  align-items: center;
  padding: 15px;
  background: var(--surface-ground);
  border-radius: 8px;
  border: 1px solid var(--surface-border);
}

.milestone-date {
  background: var(--primary-color);
  color: #ffffff; /* Set text color to white */
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  min-width: 80px;
  text-align: center;
}

.milestone-content {
  flex: 1;
}

.milestone-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.milestone-description {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
  line-height: 1.4;
}

.milestone-details {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.milestone-text {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
  padding: 4px 8px;
  border: 1px solid var(--surface-border);
  border-radius: 4px;
  background-color: var(--surface-card);
}

/* Returns Configuration */
.returns-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.return-category-card {
  background: var(--surface-ground);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  padding: 16px;
}

.return-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.goal-returns-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}

.goal-return-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: var(--surface-ground);
  border: 1px solid var(--surface-border);
  border-radius: 6px;
  min-height: 70px;
  overflow: visible;
  position: relative;
  contain: layout style;
}

.goal-return-item .goal-info {
  flex: 1;
  min-width: 0;
  margin-right: 16px;
}

.goal-return-item .return-input {
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}

/* Custom Chart Legend */
.custom-chart-legend {
  padding: 2px 0;
  margin: 0;
}

.legend-item {
  padding: 0;
  margin: 0;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}

.legend-dot-dashed {
  width: 14px;
  height: 2px;
  border-radius: 0;
  border: 2px dashed;
  background: transparent !important;
}

.legend-star {
  color: rgb(34, 197, 94);
  font-size: 0.75rem;
  line-height: 1;
}

.font-11 {
  font-size: 0.6875rem;
}

.ml-5 {
  margin-left: 5px;
}

.mb-3 {
  margin-bottom: 6px;
}

/* Legacy styles for backward compatibility */
.legacy-layout {
  margin-top: 20px;
}

.note {
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: 8px;
  padding: 20px;
}

.note ol {
  margin: 0;
  padding-left: 20px;
}

.note li {
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--text-color-secondary);
  margin-bottom: 8px;
}

/* Chart Tabs */
.chart-tabs {
  border-bottom: 2px solid var(--surface-border);
}

.chart-tab {
  padding: 10px 20px;
  border: none;
  background: transparent;
  color: var(--text-color-secondary);
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  border-bottom: 3px solid transparent;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  margin-bottom: -2px;
}

.chart-tab:hover {
  color: var(--text-color);
  background: var(--surface-hover);
}

.chart-tab.active {
  color: var(--primary-color);
  border-bottom-color: var(--primary-color);
  font-weight: 600;
}

.chart-tab i {
  font-size: 0.875rem;
}

.chart-info {
  border: 1px solid var(--surface-border);
}

.info-item {
  display: flex;
  align-items: center;
  gap: 5px;
}

.mr-5 {
  margin-right: 5px;
}

/* Chart Improvements */
.chart-wrapper {
  gap: 20px;
}

.chart-wrapper > div {
  flex: 1;
  min-width: 300px;
}

.line {
  flex: 1;
  width: 100%;
}

.chart-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
}

.chart-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: var(--text-color-secondary);
  text-align: center;
}

.chart-placeholder i {
  margin-bottom: 10px;
  opacity: 0.5;
}

.sip-value-display {
  text-align: center;
  margin-bottom: 15px;
  padding: 15px;
  background: var(--surface-ground);
  border-radius: 8px;
}

.custom-legend {
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  margin-top: 15px;
}

.legend-item {
  align-items: center;
  padding: 8px;
}

/* SIP Cards */
.sip-cards-wrapper {
  margin-bottom: 20px;
}

.sip-metrics {
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.metric-card {
  transition: transform 0.2s ease;
  min-height: 120px;
  justify-content: center;
}

.metric-card:hover {
  transform: translateY(-2px);
}

/* Enhanced Responsive Design */
@media (max-width: 1024px) {
  .chart-wrapper {
    flex-direction: column;
  }
}

@media (max-width: 768px) {
  .goal-dashboard {
    padding: 16px;
  }

  .no-goals-motivational {
    min-height: 400px;
    margin-top: 20px;
  }

  .motivational-card {
    padding: 30px 20px;
  }

  .motivational-card h2 {
    font-size: 1.5rem;
  }

  .goal-benefits {
    gap: 15px;
  }

  .benefit-icon {
    width: 40px;
    height: 40px;
  }

  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .chart-wrapper {
    gap: 15px;
  }

  .chart-tab {
    padding: 8px 16px;
    font-size: 0.8rem;
  }

  .overview-cards {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .goals-grid {
    grid-template-columns: 1fr;
  }

  .sip-metrics {
    grid-template-columns: repeat(2, 1fr);
  }

  .goal-return-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    padding: 16px;
    overflow: visible;
  }

  .goal-return-item .goal-info {
    margin-right: 0;
    margin-bottom: 8px;
  }

  .goal-return-item .return-input {
    width: 100%;
    justify-content: space-between;
  }

  .milestone-item {
    flex-direction: column;
    align-items: flex-start;
  }

  .milestone-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .allocation-note {
    flex-direction: column;
    gap: 16px;
    padding: 20px;
  }

  .icon-container {
    align-self: flex-start;
    min-width: 40px;
    height: 40px;
  }

  .methodology-item, .methodology-insight {
    flex-direction: column;
    text-align: center;
    padding: 12px;
  }

  .method-icon, .insight-icon {
    align-self: center;
  }
}

@media (max-width: 480px) {
  .overview-cards {
    grid-template-columns: 1fr;
  }

  .sip-metrics {
    grid-template-columns: 1fr;
  }

  .chart-tab {
    padding: 6px 12px;
    font-size: 0.75rem;
  }
}

/* Allocation Methodology Enhanced Styles */
.allocation-methodology-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid var(--surface-border);
  overflow: hidden;
  transition: all 0.3s ease;
}

.allocation-methodology-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.methodology-header {
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.3s ease;
  background: linear-gradient(to right, rgba(var(--primary-rgb), 0.02), transparent);
}

.methodology-header:hover {
  background: linear-gradient(to right, rgba(var(--primary-rgb), 0.05), transparent);
}

.header-content-wrapper {
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1;
  text-align: left;
}

.icon-wrapper {
  position: relative;
  flex-shrink: 0;
}

.icon-circle {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-color-dark, var(--primary-color)));
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(var(--primary-rgb), 0.3);
  transition: all 0.3s ease;
}

.methodology-header:hover .icon-circle {
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(var(--primary-rgb), 0.4);
}

.icon-circle i {
  font-size: 1.5rem;
  color: white;
}

.header-text {
  flex: 1;
  text-align: left;
}

.methodology-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--text-color);
  margin: 0 0 4px 0;
  text-align: left;
  display: block;
}

.methodology-subtitle {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
  margin: 0;
  transition: color 0.3s ease;
  text-align: left;
}

.methodology-header:hover .methodology-subtitle {
  color: var(--primary-color);
}

.chevron-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--surface-ground);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.chevron-icon i {
  font-size: 1.125rem;
  color: var(--primary-color);
  transition: transform 0.3s ease;
}

.methodology-header:hover .chevron-icon {
  background: var(--primary-color);
}

.methodology-header:hover .chevron-icon i {
  color: white;
}

/* Expand Transition */
.methodology-expand-enter-active,
.methodology-expand-leave-active {
  transition: all 0.4s ease;
  max-height: 1000px;
}

.methodology-expand-enter-from,
.methodology-expand-leave-to {
  opacity: 0;
  max-height: 0;
}

.methodology-content {
  padding: 0 24px 24px 24px;
}

.methodology-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.methodology-card {
  background: white;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid var(--surface-border);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.methodology-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, transparent, currentColor, transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.methodology-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.methodology-card:hover::before {
  opacity: 1;
}

.proportional-card {
  color: #16a34a;
}

.unified-card {
  color: #0ea5e9;
}

.insight-card {
  color: #f59e0b;
}

.card-icon-wrapper {
  margin-bottom: 16px;
}

.card-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.methodology-card:hover .card-icon {
  transform: scale(1.1) rotate(5deg);
}

.card-icon i {
  font-size: 1.25rem;
  color: white;
}

.bg-gradient-success {
  background: linear-gradient(135deg, #16a34a, #15803d);
  box-shadow: 0 4px 12px rgba(22, 163, 74, 0.3);
}

.bg-gradient-info {
  background: linear-gradient(135deg, #0ea5e9, #0284c7);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

.bg-gradient-warning {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
}

.card-content {
  position: relative;
  z-index: 1;
}

.card-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.title-icon {
  font-size: 1.125rem;
}

.card-description {
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--text-color-secondary);
  margin: 0;
}

.highlight-amount {
  display: inline-block;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-color-dark, var(--primary-color)));
  color: white;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 0.875rem;
}

.highlight-text {
  color: var(--primary-color);
  font-weight: 600;
  position: relative;
}

.highlight-text::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--primary-color), transparent);
  opacity: 0.3;
}

/* Responsive adjustments for methodology */
@media (max-width: 768px) {
  .methodology-grid {
    grid-template-columns: 1fr;
  }

  .header-content-wrapper {
    gap: 12px;
  }

  .icon-circle {
    width: 48px;
    height: 48px;
  }

  .icon-circle i {
    font-size: 1.25rem;
  }

  .methodology-title {
    font-size: 1rem;
  }

  .methodology-card {
    padding: 20px;
  }
}
</style>
